import { useEffect, useState } from "react";
import api, { fmtRM, fmtDate } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Users, UserCheck, BookOpenCheck, UserRound, TrendingUp, Wallet, AlertCircle, Armchair
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell
} from "recharts";

const PIPELINE_COLORS = ["#3b82f6", "#6366f1", "#a855f7", "#f59e0b", "#f97316", "#14b8a6", "#10b981", "#22c55e", "#0ea5e9"];

export default function Dashboard() {
  const { t } = useLang();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="w-8 h-8 border-4 border-brand-800 border-t-gold-500 rounded-full animate-spin" />
      </div>
    );
  }

  const k = stats.kpis;
  const kpis = [
    { key: "leads", label: t("dash.leads"), value: k.leads, icon: Users, fmt: (v) => v },
    { key: "qualified", label: t("dash.qualified"), value: k.qualified, icon: UserCheck, fmt: (v) => v },
    { key: "bookings", label: t("dash.bookings"), value: k.bookings, icon: BookOpenCheck, fmt: (v) => v },
    { key: "pax", label: t("dash.pax"), value: k.pax, icon: UserRound, fmt: (v) => v },
    { key: "sales", label: t("dash.sales"), value: k.sales, icon: TrendingUp, fmt: fmtRM, gold: true },
    { key: "collection", label: t("dash.collection"), value: k.collection, icon: Wallet, fmt: fmtRM, gold: true },
    { key: "outstanding", label: t("dash.outstanding"), value: k.outstanding, icon: AlertCircle, fmt: fmtRM },
    { key: "seats", label: t("dash.seats"), value: k.available_seats, icon: Armchair, fmt: (v) => v },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="font-sans text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">{t("app.dashboard")}</h1>
        <p className="text-sm text-muted-foreground mt-1">Live operations overview — real-time agency data</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {kpis.map(({ key, label, value, icon: Icon, fmt, gold }) => (
          <Card key={key} data-testid={`dashboard-kpi-${key}`} className="pro-card">
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[10px] sm:text-[11px] font-mono uppercase tracking-wider text-muted-foreground truncate">{label}</div>
                  <div className={`mt-2 font-sans text-xl sm:text-2xl font-bold tabular-nums ${gold ? "text-gold-700" : "text-slate-900"}`}>{fmt(value)}</div>
                </div>
                <div className={`w-9 h-9 rounded-xl border flex items-center justify-center shrink-0 ${gold ? "bg-gold-100/60 border-gold-500/25" : "bg-emerald-50 border-emerald-100"}`}>
                  <Icon className={`w-4 h-4 ${gold ? "text-gold-600" : "text-brand-700"}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 pro-card" data-testid="revenue-chart">
          <CardHeader className="pb-2"><CardTitle className="text-base font-sans font-semibold">{t("dash.revenue")}</CardTitle></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%" minHeight={180}>
              <AreaChart data={stats.revenue_by_month} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gSales" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8C6D3F" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#8C6D3F" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gCol" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C9A961" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#C9A961" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E2D8" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `${v / 1000}k`} />
                <Tooltip formatter={(v) => fmtRM(v)} />
                <Area type="monotone" dataKey="sales" stroke="#8C6D3F" fill="url(#gSales)" strokeWidth={2} name="Sales" />
                <Area type="monotone" dataKey="collection" stroke="#C9A961" fill="url(#gCol)" strokeWidth={2} name="Collection" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="pro-card" data-testid="pipeline-chart">
          <CardHeader className="pb-2"><CardTitle className="text-base font-sans font-semibold">{t("dash.pipeline")}</CardTitle></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%" minHeight={180}>
              <BarChart data={stats.pipeline} layout="vertical" margin={{ left: 8, right: 12 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="status" width={90} tick={{ fontSize: 10 }} stroke="#94a3b8"
                  tickFormatter={(s) => s.replace(/_/g, " ")} />
                <Tooltip />
                <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                  {stats.pipeline.map((_, i) => <Cell key={i} fill={PIPELINE_COLORS[i % PIPELINE_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 pro-card" data-testid="upcoming-departures">
          <CardHeader className="pb-3"><CardTitle className="text-base font-sans font-semibold">{t("dash.upcoming")}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {stats.upcoming_departures.map((d) => {
              const pct = Math.round(((d.sold + d.reserved) / d.total_seats) * 100);
              return (
                <div key={d.departure_id} data-testid={`departure-row-${d.departure_id}`}>
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="font-medium text-brand-950 truncate mr-3">{d.package}</span>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(d.date)}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Progress value={pct} className="h-2 flex-1" />
                    <span className={`text-xs font-mono whitespace-nowrap ${d.available <= 5 ? "text-destructive font-semibold" : "text-muted-foreground"}`}>
                      {d.available} left
                    </span>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card className="pro-card" data-testid="top-packages">
          <CardHeader className="pb-3"><CardTitle className="text-base font-sans font-semibold">{t("dash.top")}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {stats.top_packages.map((p, i) => (
              <div key={p.name} className="flex items-center gap-3" data-testid={`top-package-${i}`}>
                <span className="w-7 h-7 rounded-lg bg-brand-800 text-sand text-xs font-mono flex items-center justify-center shrink-0">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-brand-950 truncate">{p.name}</div>
                  <div className="text-xs text-muted-foreground">{p.pax} pax • {fmtRM(p.revenue)}</div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="pro-card" data-testid="branch-performance">
          <CardHeader className="pb-3"><CardTitle className="text-base font-sans font-semibold">{t("dash.branch")}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {stats.branch_performance.map((b) => {
              const pct = Math.min(100, Math.round((b.sales / b.target) * 100));
              return (
                <div key={b.name} data-testid={`branch-row-${b.name.replace(/\s/g, "-")}`}>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="font-medium text-brand-950">{b.name}</span>
                    <span className="text-xs font-mono text-muted-foreground">{fmtRM(b.sales)} / {fmtRM(b.target)} • {pct}%</span>
                  </div>
                  <Progress value={pct} className="h-2" />
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card className="pro-card" data-testid="recent-bookings">
          <CardHeader className="pb-3"><CardTitle className="text-base font-sans font-semibold">{t("dash.recent")}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {stats.recent_bookings.map((b) => (
              <div key={b.booking_id} className="flex items-center justify-between gap-3" data-testid={`recent-booking-${b.booking_no}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-brand-950 truncate">{b.customer_name}</div>
                  <div className="text-xs text-muted-foreground truncate">{b.booking_no} • {b.package_name}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-mono text-brand-950">{fmtRM(b.total)}</div>
                  <div className={`text-[10px] font-mono uppercase ${b.outstanding > 0 ? "text-gold-700" : "text-emerald-700"}`}>
                    {b.outstanding > 0 ? `${fmtRM(b.outstanding)} due` : "paid"}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
